import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { LEGACY_DATA_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import {
    AccountChangeEmailFormData,
    AccountEditPasswordFormData,
    AccountEditProfileFormData,
    AccountFormResponseData,
    AccountUpgradeAgentAddonResponse,
    MFAManagementCheckPasswordFormData,
} from 'AccountsApp/ts/interfaces/account-settings.interface';
import { BenchmarkIndex, ClientGroupPreference } from 'AccountsApp/ts/interfaces/client-group-preference.interface';
import { MFADeviceCollectionResponse } from 'AccountsApp/ts/interfaces/mfa.interface';
import { ReferFriendsPostResponse } from 'AccountsApp/ts/interfaces/refer-friends-post-response.interface';
import { UserPreference } from 'AccountsApp/ts/interfaces/user-preference.interface';
import { UserSecurityBenchmarkPreference } from 'AccountsApp/ts/interfaces/user-security-benchmark-preference.interface';
import { LooseObject } from 'Angular/interfaces/loose-object.interface';

import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';

@Injectable({
    providedIn: 'root',
})
export class AccountsService {
    private cachedUserPreferences: Observable<UserPreference>;

    private cachedClientGroupPreferences: Observable<ClientGroupPreference>;

    constructor(private ajaxService: AjaxService, private httpClient: HttpClient, private urlsService: UrlsService) {
        // For anonymous users, skip API calls and return empty observables to improve page performance.
        if (window.USER.authenticated) {
            this.cachedUserPreferences = this.fetchUserPreferences();
            this.cachedClientGroupPreferences = this.fetchClientGroupPreferences();
        } else {
            this.cachedUserPreferences = of({} as UserPreference);
            this.cachedClientGroupPreferences = of({} as ClientGroupPreference);
        }
    }

    private fetchUserPreferences(): Observable<UserPreference> {
        const userPreferencesUrl = this.urlsService.ACCOUNTS.USER_PREFERENCES;
        return this.ajaxService.getDataObserveBody<UserPreference>(userPreferencesUrl).pipe(
            map((preferences) => {
                if (preferences == null) {
                    throw new Error(`Empty response body from ${userPreferencesUrl}`);
                }
                return preferences;
            }),
            // shareReplay allows multiple subscribers to subscribe to this observable
            // without triggering additional HTTP requests.
            shareReplay(1)
        );
    }

    private fetchClientGroupPreferences(): Observable<ClientGroupPreference> {
        const clientGroupPreferencesUrl = this.urlsService.ACCOUNTS.CLIENT_GROUP_PREFERENCES;
        return this.ajaxService.getDataObserveBody<ClientGroupPreference>(clientGroupPreferencesUrl).pipe(
            map((preferences) => {
                if (preferences == null) {
                    throw new Error(`Empty response body from ${clientGroupPreferencesUrl}`);
                }
                return preferences;
            }),
            // shareReplay allows multiple subscribers to subscribe to this observable
            // without triggering additional HTTP requests.
            shareReplay(1)
        );
    }

    getClientGroupPreferences(): Observable<ClientGroupPreference> {
        return this.cachedClientGroupPreferences;
    }

    getUserPreferences(): Observable<UserPreference> {
        return this.cachedUserPreferences;
    }

    getUserSecurityBenchmarkPreferences(security_id: string): Observable<UserSecurityBenchmarkPreference> {
        // For anonymous users, return empty default preferences
        if (!window.USER.authenticated) {
            return of({
                security_id,
                benchmark_name: '',
                benchmark_security_id: '',
                use_enhanced_benchmark: false,
            } as UserSecurityBenchmarkPreference);
        }
        const url = this.urlsService.ACCOUNTS.USER_SECURITY_BENCHMARK_PREFERENCES;
        const getParams = { security_id };
        return this.ajaxService.getDataObserveBody<UserSecurityBenchmarkPreference>(url, getParams);
    }

    postReferFriends(emails: string): Observable<HttpResponse<ReferFriendsPostResponse>> {
        const url = this.urlsService.ACCOUNTS.REFER_FRIEND;
        const postParams = { emails };
        return this.ajaxService.postFormEncodedDataObserveResponse<ReferFriendsPostResponse>(url, postParams);
    }

    putUserPreferences(putParams: LooseObject): Observable<{}> {
        const url = this.urlsService.ACCOUNTS.USER_PREFERENCES;
        return this.ajaxService.putDataObserveBody<{}>(url, putParams);
    }

    putUserSecurityBenchmarkPreferences(putParams: LooseObject): Observable<{}> {
        const url = this.urlsService.ACCOUNTS.USER_SECURITY_BENCHMARK_PREFERENCES;
        return this.ajaxService.putDataObserveBody<{}>(url, putParams);
    }

    putUserPreferencesAndRefreshCache(
        putParams: LooseObject,
        errorCallback?: (error?: any) => void,
        successCallback?: () => void
    ): void {
        this.putUserPreferences(putParams).subscribe({
            next: () => {
                this.cachedUserPreferences = this.fetchUserPreferences();
                successCallback?.();
            },
            error: errorCallback,
        });
    }

    updateProfile(profileData: AccountEditProfileFormData): Observable<HttpResponse<AccountFormResponseData>> {
        const url = this.urlsService.ACCOUNTS.ACCOUNT_PROFILE;
        return this.ajaxService.postFormEncodedDataObserveBody(url, profileData);
    }

    updatePassword(passwordData: AccountEditPasswordFormData): Observable<HttpResponse<AccountFormResponseData>> {
        const url = this.urlsService.ACCOUNTS.CHANGE_PASSWORD;
        return this.ajaxService.postFormEncodedDataObserveBody(url, passwordData);
    }

    createChangeEmailRequest(
        changeEmailData: AccountChangeEmailFormData
    ): Observable<HttpResponse<AccountFormResponseData>> {
        const url = this.urlsService.ACCOUNTS.CHANGE_EMAIL;
        return this.ajaxService.postFormEncodedDataObserveBody(url, changeEmailData);
    }

    updateRiskScoreHypotheticalReturns(
        hypotheticalReturns: BenchmarkIndex[],
        restoreToDefault = false
    ): Observable<{ benchmark_objects: BenchmarkIndex[] }> {
        const url = this.urlsService.ACCOUNTS.RISK_SCORE_BENCHMARK;
        return this.ajaxService.putJsonDataObserveBody(url, { benchmarks: hypotheticalReturns, restoreToDefault });
    }

    getMFADevices(): Observable<MFADeviceCollectionResponse> {
        const url = this.urlsService.ACCOUNTS.MFA_DEVICE_COLLECTION;
        return this.ajaxService.getDataObserveBody<MFADeviceCollectionResponse>(url);
    }

    manageMFADevices(postData: MFAManagementCheckPasswordFormData): Observable<HttpResponse<AccountFormResponseData>> {
        const url = this.urlsService.ACCOUNTS.MFA_DEVICE_MANAGEMENT;
        return this.ajaxService.postFormEncodedDataObserveBody(url, postData);
    }

    useGenericData(overrides?: string[]): Observable<boolean> {
        const requiredFeatures = [...(overrides ?? []), LEGACY_DATA_FEATURE];
        if (window.USER.features.find((feature) => requiredFeatures.includes(feature)) == null) {
            return of(true);
        }

        return this.getUserPreferences().pipe(
            map((preferences) => preferences.use_generic_data_display),
            catchError(() => of(true))
        );
    }

    changeAgentAddon(tierId: number | 'base'): Observable<AccountUpgradeAgentAddonResponse | {}> {
        const url = this.urlsService.ACCOUNTS.UPGRADE_AGENT_ADDON;
        return this.ajaxService.postFormEncodedDataObserveBody<AccountUpgradeAgentAddonResponse | {}>(url, {
            tier_id: String(tierId),
        });
    }
}
